import { type EnumUserRoles } from 'entities/Users'
import { type Integration } from 'entities/Integrations/model/Integration'
import type { IEntitiesStore } from 'entities/EntitiesExport'

const appID = '5f7b3eb7e197fd4f8b4c70a4'

// Local flag to track if Canny has been initialized in this module
let isCannyInitialized = false

interface CannyQueueFunction {
  (...args: unknown[]): void
  q: unknown[][]
}

// Fallback function to load Canny SDK if it's not available
const loadCannySDKFallback = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Setup Canny queue first, regardless of script existence
    if (typeof window.Canny !== 'function') {
      const cannyQueue: CannyQueueFunction = function (...args: unknown[]) {
        cannyQueue.q.push(args)
      } as CannyQueueFunction
      cannyQueue.q = []
      window.Canny = cannyQueue
    }

    // Check if script already exists
    if (document.getElementById('canny-jssdk')) {
      // Script exists, wait a bit more for it to load
      setTimeout(() => resolve(), 1000)
      return
    }

    // Create script element
    const script = document.createElement('script')
    script.id = 'canny-jssdk'
    script.type = 'text/javascript'
    script.async = true
    script.src = 'https://canny.io/sdk.js'

    script.onload = () => {
      resolve()
    }

    script.onerror = (error) => {
      reject(error)
    }

    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
    } else {
      document.head.appendChild(script)
    }
  })
}

// Quick check for global flag only (no DOM queries)
const getGlobalCannyFlag = (): boolean => {
  return !!window.__CANNY_INITIALIZED__ || isCannyInitialized
}

// Check if Canny was already initialized by any part of the application
const getGlobalCannyState = (): boolean => {
  // Check our global flag
  const hasGlobalFlag = getGlobalCannyFlag()

  // Also check if Canny widget is already active
  const cannyChangelogElement = document.querySelector('[data-canny-changelog]')
  const cannyTriggerElement = document.querySelector('.canny-changelog-trigger')
  const hasCannyWidget = !!window.Canny && !!(cannyChangelogElement || cannyTriggerElement)

  return hasGlobalFlag || hasCannyWidget
}

// Set global Canny state to prevent other parts from initializing
const setGlobalCannyState = (state: boolean): void => {
  window.__CANNY_INITIALIZED__ = state
  isCannyInitialized = state
}

export type ICannyProps = {
  appID: string
  position?: 'right' | 'left'
  align?: 'bottom'
  user?: {
    email: string | null
    name: string | null
    id: number
    avatarURL: string | null
    created: string
    companies: [
      {
        created: string | null
        id: number | null
        name: string | null
      }
    ]
    customFields: {
      role: EnumUserRoles
      company: string | null
      integration: Integration[]
      plan?: string
    }
  }
}

export const initChangeLogCanny = () => {
  if (!window.Canny) return

  // Check global initialization state to prevent conflicts with other apps
  if (getGlobalCannyState()) {
    return
  }

  try {
    window.Canny('initChangelog', {
      appID: '5f7b3eb7e197fd4f8b4c70a4',
      position: 'right',
      align: 'bottom',
    })

    // Mark as globally initialized
    setGlobalCannyState(true)
  } catch (error) {
    console.error('[REACT-APP] Error initializing Canny changelog:', error)
  }
}

export const initCanny = async (entities: IEntitiesStore) => {
  // Quick check if already initialized before any delays
  if (getGlobalCannyFlag()) {
    return
  }

  // Add delay to allow Vue.js app to initialize first
  await new Promise((resolve) => setTimeout(resolve, 200))

  // Check again after delay - Vue.js might have initialized during the wait
  if (getGlobalCannyFlag()) {
    return
  }

  // Check if Canny SDK is available
  if (!window.Canny) {
    await loadCannySDKFallback()

    if (!window.Canny) {
      return
    }
  }

  // Prevent multiple initializations across all apps (full check with DOM)
  if (getGlobalCannyState()) {
    return
  }

  try {
    await Promise.all([entities.usersStore.initPromise, entities.organizationStore.initPromise])
    const { user } = entities.usersStore

    if (!user) {
      return
    }

    // Check again after async operations in case Vue app initialized during waiting
    if (getGlobalCannyState()) {
      return
    }

    const cannyProps: ICannyProps = {
      appID,
      user: {
        email: user.email,
        name: user.name,
        id: user.id,
        avatarURL: user.photo_url,
        created: user.createdAt,
        companies: [
          {
            created: entities.organizationStore.created_at,
            id: entities.organizationStore.id,
            name: entities.organizationStore.name,
          },
        ],
        customFields: {
          role: user.role,
          company: entities.organizationStore.name,
          integration: entities.integrationsStore.integrations,
          plan: entities.subscriptionStore.plan?.name,
        },
      },
    }

    // Final check before calling identify - Vue.js might have initialized during async operations
    if (getGlobalCannyFlag()) {
      return
    }

    // Identify user and initialize changelog in callback to avoid double initialization
    window.Canny('identify', cannyProps, () => {
      // Double-check global state inside callback to prevent race conditions
      if (getGlobalCannyState()) {
        return
      }
      // Initialize changelog only once after successful user identification
      window.Canny?.('initChangelog', {
        appID,
        position: 'right',
        align: 'bottom',
      })

      // Mark as globally initialized
      setGlobalCannyState(true)
    })
  } catch (error) {
    console.error('[REACT-APP] Error initializing Canny:', error)
  }
}

export const resetCanny = () => {
  setGlobalCannyState(false)
}
